import React from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  AutoAwesome,
  CalendarMonth,
  Hub,
  Insights,
  Language,
  Schedule,
} from '@mui/icons-material';
import { useAppSelector } from '../../store/hooks';
import { BRAND } from '../../constants/branding';
import { BrandHeroWordmark } from '../../components/Brand/BrandHeroWordmark';
import { darkCardSx, glowOrbSx, gradientButtonSx } from '../../constants/brandStyles';

const features = [
  {
    icon: <AutoAwesome sx={{ fontSize: 32, color: BRAND.colors.primary }} />,
    title: 'AI post generation',
    description: 'Turn ideas into platform-ready posts for LinkedIn, X, and Instagram in seconds.',
  },
  {
    icon: <CalendarMonth sx={{ fontSize: 32, color: BRAND.colors.primary }} />,
    title: 'Smart scheduling',
    description: 'Plan your content calendar and publish automatically at the right time.',
  },
  {
    icon: <Hub sx={{ fontSize: 32, color: BRAND.colors.primary }} />,
    title: 'Multi-platform publishing',
    description: 'Connect your accounts once and publish everywhere from a single workflow.',
  },
  {
    icon: <Insights sx={{ fontSize: 32, color: BRAND.colors.primary }} />,
    title: 'Analytics & trends',
    description: 'Discover what is trending and track how your content performs.',
  },
  {
    icon: <Language sx={{ fontSize: 32, color: BRAND.colors.primary }} />,
    title: 'Brand consistency',
    description: 'Keep your voice, tone, and messaging aligned across every channel.',
  },
  {
    icon: <Schedule sx={{ fontSize: 32, color: BRAND.colors.primary }} />,
    title: 'Queue & monitor',
    description: 'Watch generation jobs and scheduled posts in real time from your dashboard.',
  },
];

const steps = [
  { step: '1', title: 'Create your account', body: 'Sign up with email verification in under a minute.' },
  { step: '2', title: 'Connect platforms', body: 'Link LinkedIn, X, and Instagram with secure OAuth.' },
  { step: '3', title: 'Generate & publish', body: 'Use AI to draft, refine, schedule, and post — on autopilot.' },
];

export const LandingPage: React.FC = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          py: { xs: 6, md: 10 },
          textAlign: 'center',
        }}
      >
        <Box sx={glowOrbSx(400, '-10%', '60%', 0.18)} />
        <Box sx={glowOrbSx(300, '50%', '-15%', 0.12)} />

        <Container maxWidth="md" sx={{ position: 'relative' }}>
          {/* <Chip
            label={`${BRAND.domain} · ${BRAND.since}`}
            sx={{
              mb: 4,
              bgcolor: 'rgba(0, 212, 255, 0.08)',
              color: BRAND.colors.primary,
              border: `1px solid ${BRAND.colors.border}`,
            }}
          /> */}

          <Box sx={{ mb: { xs: 4, md: 5 } }}>
            <BrandHeroWordmark showMark markHeight={80} />
          </Box>

          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.75rem', md: '2.75rem' },
              lineHeight: 1.2,
              letterSpacing: '-0.03em',
              maxWidth: 680,
              mx: 'auto',
            }}
          >
            Social content that writes, schedules, and ships itself.
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mt: 2.5,
              color: BRAND.colors.textMuted,
              maxWidth: 520,
              mx: 'auto',
              fontWeight: 400,
              fontSize: { xs: '1rem', md: '1.15rem' },
            }}
          >
            {BRAND.description}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 5 }}>
            <Button component={RouterLink} to="/register" variant="contained" size="large" sx={{ ...gradientButtonSx, px: 4, py: 1.5 }}>
              Start free
            </Button>
            <Button
              component={RouterLink}
              to="/login"
              variant="outlined"
              size="large"
              sx={{
                borderColor: BRAND.colors.border,
                color: BRAND.colors.text,
                px: 4,
                py: 1.5,
                '&:hover': { borderColor: BRAND.colors.primary, bgcolor: 'rgba(0,212,255,0.06)' },
              }}
            >
              Sign in
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Features */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography
          variant="overline"
          sx={{ color: BRAND.colors.primary, fontWeight: 700, letterSpacing: '0.12em' }}
        >
          Everything you need
        </Typography>
        <Typography variant="h4" fontWeight={800} sx={{ mt: 1, mb: 1 }}>
          One platform for your entire social workflow
        </Typography>
        <Typography sx={{ mb: 5, maxWidth: 560, color: BRAND.colors.textDim }}>
          From first draft to published post — {BRAND.name} covers generation, scheduling,
          integrations, and insights.
        </Typography>
        <Grid container spacing={3}>
          {features.map((f) => (
            <Grid item xs={12} sm={6} md={4} key={f.title}>
              <Card sx={{ ...darkCardSx, height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ mb: 2 }}>{f.icon}</Box>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {f.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: BRAND.colors.textDim, lineHeight: 1.7 }}>
                    {f.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How it works */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: BRAND.colors.bgElevated, borderTop: `1px solid ${BRAND.colors.border}`, borderBottom: `1px solid ${BRAND.colors.border}` }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={800} textAlign="center" gutterBottom>
            How {BRAND.name} works
          </Typography>
          <Typography textAlign="center" sx={{ mb: 5, color: BRAND.colors.textDim }}>
            Three steps from signup to your first scheduled post.
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {steps.map((s) => (
              <Grid item xs={12} md={4} key={s.step}>
                <Box textAlign="center">
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: '50%',
                      background: BRAND.gradient,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: '1.25rem',
                      mx: 'auto',
                      mb: 2,
                      boxShadow: `0 4px 20px ${BRAND.colors.glow}`,
                    }}
                  >
                    {s.step}
                  </Box>
                  <Typography variant="h6" fontWeight={700}>
                    {s.title}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: BRAND.colors.textDim }}>
                    {s.body}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 10 }, textAlign: 'center' }}>
        <Typography variant="h4" fontWeight={800} gutterBottom>
          Ready to grow your social presence?
        </Typography>
        <Typography sx={{ mb: 4, color: BRAND.colors.textDim }}>
          Join {BRAND.name} and start creating smarter content today.
        </Typography>
        <Button component={RouterLink} to="/register" variant="contained" size="large" sx={{ ...gradientButtonSx, px: 5 }}>
          Get started — it&apos;s free
        </Button>
      </Container>
    </>
  );
};

export default LandingPage;
